import { atom, useAtom } from "jotai";
import { supabase } from "../lib/supabase";
import { useEffect, useCallback } from "react";
import type { Database, Json } from "../types/supabase";

type Period = Database["public"]["Tables"]["periods"]["Row"];
type Product = Database["public"]["Tables"]["products"]["Row"];
type Brand = Database["public"]["Tables"]["brands"]["Row"];
type Supplier = Database["public"]["Tables"]["suppliers"]["Row"];
type StoreSet = Database["public"]["Tables"]["store_sets"]["Row"];
type StoresStoreSet = Database["public"]["Tables"]["stores_store_sets"]["Row"];

type MarketingActivityRow =
  Database["public"]["Tables"]["marketing_activities"]["Row"];

type StoreSetWithCount = StoreSet & {
  stores_store_sets?: StoresStoreSet[];
};

type StoreActivityRow = Database["public"]["Tables"]["store_activities"]["Row"];
type MarketingActivityType =
  Database["public"]["Tables"]["marketing_activity_types"]["Row"];

export type MarketingActivity = MarketingActivityRow & {
  period?: Period | null;
  product?:
    | (Product & {
        brand?:
          | (Brand & {
              supplier?: Supplier | null;
            })
          | null;
      })
    | null;
  marketing_activity_type?: MarketingActivityType | null;
  marketing_activities_store_sets?: {
    store_set?: StoreSetWithCount | null;
  }[];
  store_activities?: StoreActivityRow[];
};

const marketingActivitiesAtom = atom<MarketingActivity[]>([]);

export function useMarketingActivities() {
  const [activities, setActivities] = useAtom(marketingActivitiesAtom);

  const fetchActivities = useCallback(async () => {
    try {
      const { error, data } = await supabase
        .from("marketing_activities")
        .select(
          `
          *,
          period:periods(*),
          product:products(
            *,
            brand:brands(
              *,
              supplier:suppliers(*)
            )
          ),
          marketing_activity_type:marketing_activity_types(*),
          marketing_activities_store_sets(
            store_set:store_sets(
              *,
              stores_store_sets(*)
            )
          ),
          store_activities(*)
        `,
        )
        .eq("is_deleted", false);

      if (error) {
        console.error("Error fetching marketing activities:", error);
        throw error;
      }

      setActivities((data || []) as MarketingActivity[]);
    } catch (e) {
      console.error("Failed to fetch marketing activities:", e);
    }
  }, [setActivities]);

  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  return [activities, fetchActivities] as const;
}

/**
 * Soft delete a marketing activity using the DB function.
 * The DB function handles the entire process atomically:
 * 1. Check for proofs on non-deleted store_activities (fails if any exist)
 * 2. Hard delete any proofs related to this MA's store_activities
 * 3. Soft delete store_activities
 * 4. Soft delete the marketing_activity
 */
export async function deleteMarketingActivity(
  activityId: number,
): Promise<{ success: boolean; error?: string }> {
  try {
    const { error } = await supabase.rpc("soft_delete_marketing_activity", {
      p_marketing_activity_id: activityId,
    });

    if (error) {
      // Extract meaningful error message from Postgres error
      const message = error.message.includes("Cannot delete")
        ? "Cannot delete: proofs exist for related store activities"
        : error.message;
      return { success: false, error: message };
    }

    return { success: true };
  } catch (e) {
    return {
      success: false,
      error: e instanceof Error ? e.message : "Unknown error",
    };
  }
}

/**
 * Fetch distinct filter values for marketing activities via server-side RPC.
 * Handles all filter keys (periods, products, suppliers, activity types).
 */
export async function fetchDistinctValuesForMarketingActivities(
  filterKey: string,
  otherFilters?: Record<string, unknown>,
): Promise<{ value: string | number; label: string }[]> {
  const { data, error } = await supabase.rpc(
    "get_distinct_values_for_marketing_activities",
    {
      p_filter_key: filterKey,
      p_other_filters: (otherFilters ?? {}) as Record<string, unknown> & Json,
    },
  );

  if (error) throw error;

  const numericKeys = new Set([
    "period_ids",
    "product_ids",
    "supplier_ids",
    "type_ids",
  ]);

  return (data ?? []).map((row: { value: string; label: string }) => ({
    value: numericKeys.has(filterKey) ? Number(row.value) : row.value,
    label: row.label,
  }));
}
